import { create } from 'zustand';
import { Capacitor } from '@capacitor/core';
import type { Coordinates } from '../types/location';
import { coordsToFrequency, frequencyToCoords } from '../lib/frequency';
import { getCurrentPosition, watchPosition } from '../lib/geolocation';
import { useConfigStore } from './configStore';

type ReceptionMode = 'blur_beyond' | 'sensitivity_only';

const SETTINGS_KEY_PREFIX = 'ourhertz_user_settings_';
/** Per-device key — tracks whether the user dismissed the custom consent modal with "Not Now". */
const USER_OPT_OUT_KEY = 'ourhertz_location_opted_out';
/** Per-session (sessionStorage) — user's opt-in choice for sharing location on the map. Resets each login / app restart. */
const SHARE_ON_MAP_KEY = 'ourhertz_share_location_on_map';
const SHARE_ON_MAP_PROMPTED_KEY = 'ourhertz_share_location_prompted';
/** Per-device (localStorage) — user has chosen "don't ask again" for the map-visibility modal. */
const DONT_ASK_MAP_CONSENT_KEY = 'ourhertz_dont_ask_map_consent';
function currentPlatformKey(): 'ios' | 'android' | 'web' {
  if (Capacitor.isNativePlatform()) {
    const p = Capacitor.getPlatform();
    if (p === 'ios') return 'ios';
    if (p === 'android') return 'android';
  }
  return 'web';
}

/**
 * Is the Apple-5.1.2 strict consent flow required on this platform?
 * Defaults to `true` (safe for App Review) until config loads or if the setting is absent.
 */
export function isMapConsentRequired(): boolean {
  try {
    const cfg = useConfigStore.getState().config;
    const map = cfg.mapConsentRequiredPlatforms;
    if (!map) return true;
    return map[currentPlatformKey()] !== false;
  } catch {
    return true;
  }
}

/** Reactive hook — returns `true` when strict Apple-5.1.2 flow applies on this platform. */
export function useMapConsentRequired(): boolean {
  return useConfigStore((s) => {
    const map = s.config.mapConsentRequiredPlatforms;
    if (!map) return true;
    return map[currentPlatformKey()] !== false;
  });
}

export { currentPlatformKey };

/**
 * Session-scoped state (shareLocationOnMap, shareLocationPrompted) must reset
 * when the session ends — which means tab close on web, process kill on native.
 *
 * On web, sessionStorage has exactly this semantic (persists on refresh, clears
 * on tab close). On Android Capacitor WebView however, sessionStorage has been
 * observed to persist across process kill+relaunch (the WebView storage survives
 * the Activity destruction). That left shareLocationPrompted=true forever after
 * the first login, blocking the map-consent popup from ever reappearing.
 *
 * Fix: on native, store these values in memory only (module state). Memory dies
 * with the JS engine, which is exactly when the process is killed. Background →
 * foreground keeps the same JS engine, so the values are preserved — matching
 * the desired "per-session" semantic on mobile. Web still uses sessionStorage.
 */
const IS_NATIVE = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();

let nativeShareOnMap = false;
let nativeSharePrompted = false;

function readInitialShareLocationOnMap(): boolean {
  // On consent-not-required (relaxed) platforms: always on — no toggle exposed, no storage.
  if (!isMapConsentRequired()) return true;
  if (IS_NATIVE) return nativeShareOnMap;
  try { return sessionStorage.getItem(SHARE_ON_MAP_KEY) === '1'; } catch { return false; }
}

function readInitialSharePrompted(): boolean {
  if (IS_NATIVE) return nativeSharePrompted;
  try { return sessionStorage.getItem(SHARE_ON_MAP_PROMPTED_KEY) === '1'; } catch { return false; }
}

function saveUserSettings(uid: string, patch: Record<string, unknown>) {
  try {
    const key = SETTINGS_KEY_PREFIX + uid;
    const prev = JSON.parse(localStorage.getItem(key) || '{}');
    localStorage.setItem(key, JSON.stringify({ ...prev, ...patch }));
  } catch {}
}

interface LocationState {
  /** Actual GPS position */
  realLocation: Coordinates | null;
  /** Effective position (teleported or real) */
  effectiveLocation: Coordinates | null;
  /** Current frequency string */
  frequency: string;
  /** Whether manual frequency (teleport) is active */
  isManualFreq: boolean;
  manualFrequency: string | null;
  /** Radar radius in km */
  radarRadius: number;
  /** Message reception radius in km */
  messageReceptionRadius: number;
  /** Message reception mode */
  receptionMode: ReceptionMode;
  /** Location sync interval in seconds */
  syncIntervalSec: number;
  /** GPS accuracy in meters */
  accuracy: number | null;
  /** Location permission granted */
  permissionGranted: boolean;
  /** Permission state: 'prompt' | 'granted' | 'denied' | 'unknown' */
  permissionState: 'prompt' | 'granted' | 'denied' | 'unknown';
  /** In-app consent: user has explicitly chosen to use (or skip) location via the custom modal. */
  userOptedOut: boolean;
  /**
   * Whether the user has opted in to show their location on the map for the current session.
   * Default: false. Resets on each login / app restart. Required by Apple Guideline 5.1.2 —
   * users must manually check-in each time before their location is displayed on the map.
   */
  shareLocationOnMap: boolean;
  /** Whether the share-on-map prompt has been shown during this session (prevents re-asking after decline). */
  shareLocationPrompted: boolean;
  /**
   * Per-device preference (localStorage): user has asked not to be prompted with the
   * map-visibility modal on future logins on this device. Does NOT auto-enable sharing;
   * the default is still "hidden" every session — Apple 5.1.2 manual check-in is preserved.
   */
  dontAskMapConsent: boolean;

  // Actions
  startWatching: () => () => void;
  fetchOnce: () => Promise<void>;
  checkPermission: () => Promise<void>;
  requestPermission: () => Promise<boolean>;
  /** Mark user as having dismissed the custom consent modal with "Not Now". */
  setUserOptedOut: (opted: boolean) => void;
  /** Set whether the user wants their location shown on the map for this session. */
  setShareLocationOnMap: (share: boolean) => void;
  /** Mark the share-on-map prompt as shown for this session. */
  setShareLocationPrompted: (shown: boolean) => void;
  /** Reset session-only sharing flags (called on logout). */
  resetShareLocationOnMap: () => void;
  /** Set the per-device "don't ask again" flag for the map-visibility modal. */
  setDontAskMapConsent: (value: boolean) => void;
  /** Re-read initial share-on-map value from storage (called after config loads). */
  rehydrateShareLocationOnMap: () => void;
  setRadarRadius: (km: number) => void;
  setMessageReceptionRadius: (km: number) => void;
  setReceptionMode: (mode: ReceptionMode) => void;
  setSyncInterval: (sec: number) => void;
  /** Load per-user settings from localStorage */
  loadUserSettings: (uid: string) => void;
  /** Reset user-specific settings (on logout) */
  resetUserSettings: () => void;
  teleport: (frequency: string, jumpRange?: { min: number; max: number }) => boolean;
  cancelTeleport: () => void;
}

/** UID of the currently loaded user — used for saving settings */
let currentUid: string | null = null;

function computeFrequency(coords: Coordinates | null): string {
  if (!coords) return 'N/A';
  return coordsToFrequency(coords.lat, coords.lng);
}

export const useLocationStore = create<LocationState>((set, get) => ({
  realLocation: null,
  effectiveLocation: null,
  frequency: 'N/A',
  isManualFreq: false,
  manualFrequency: null,
  radarRadius: 5,
  messageReceptionRadius: 100,
  receptionMode: 'blur_beyond',
  syncIntervalSec: 10,
  accuracy: null,
  permissionGranted: false,
  permissionState: 'unknown',
  userOptedOut: (() => {
    try { return localStorage.getItem(USER_OPT_OUT_KEY) === '1'; } catch { return false; }
  })(),
  shareLocationOnMap: readInitialShareLocationOnMap(),
  shareLocationPrompted: readInitialSharePrompted(),
  dontAskMapConsent: (() => {
    try { return localStorage.getItem(DONT_ASK_MAP_CONSENT_KEY) === '1'; } catch { return false; }
  })(),

  setUserOptedOut: (opted) => {
    try {
      if (opted) localStorage.setItem(USER_OPT_OUT_KEY, '1');
      else localStorage.removeItem(USER_OPT_OUT_KEY);
    } catch {}
    set({ userOptedOut: opted });
  },

  setShareLocationOnMap: (share) => {
    // Relaxed mode: always on — no user-controlled toggle.
    if (!isMapConsentRequired()) {
      set({ shareLocationOnMap: true });
      return;
    }
    if (IS_NATIVE) {
      nativeShareOnMap = share;
    } else {
      try {
        if (share) sessionStorage.setItem(SHARE_ON_MAP_KEY, '1');
        else sessionStorage.removeItem(SHARE_ON_MAP_KEY);
      } catch {}
    }
    set({ shareLocationOnMap: share });
  },
  /** Re-read the initial value from storage — used after the server config loads. */
  rehydrateShareLocationOnMap: () => {
    set({ shareLocationOnMap: readInitialShareLocationOnMap() });
  },

  setShareLocationPrompted: (shown) => {
    if (IS_NATIVE) {
      nativeSharePrompted = shown;
    } else {
      try {
        if (shown) sessionStorage.setItem(SHARE_ON_MAP_PROMPTED_KEY, '1');
        else sessionStorage.removeItem(SHARE_ON_MAP_PROMPTED_KEY);
      } catch {}
    }
    set({ shareLocationPrompted: shown });
  },

  resetShareLocationOnMap: () => {
    if (IS_NATIVE) {
      nativeShareOnMap = false;
      nativeSharePrompted = false;
    } else {
      try {
        sessionStorage.removeItem(SHARE_ON_MAP_KEY);
        sessionStorage.removeItem(SHARE_ON_MAP_PROMPTED_KEY);
      } catch {}
    }
    // Strict: reset to hidden. Relaxed: always on, toggle not exposed.
    set({
      shareLocationOnMap: !isMapConsentRequired(),
      shareLocationPrompted: false,
    });
  },

  setDontAskMapConsent: (value) => {
    try {
      if (value) localStorage.setItem(DONT_ASK_MAP_CONSENT_KEY, '1');
      else localStorage.removeItem(DONT_ASK_MAP_CONSENT_KEY);
    } catch {}
    set({ dontAskMapConsent: value });
  },

  checkPermission: async () => {
    // Native (iOS/Android): use Capacitor Geolocation plugin's checkPermissions, which
    // reads the OS-level permission state WITHOUT triggering a prompt. The previous
    // WebView-based probe called getCurrentPosition, which in native WebViews forces
    // the OS dialog to appear on every launch and usually timed out — leaving state
    // as 'prompt' forever and causing the custom in-app modal to re-appear every start.
    if (Capacitor.isNativePlatform()) {
      try {
        const { Geolocation } = await import('@capacitor/geolocation');
        const status = await Geolocation.checkPermissions();
        // The plugin returns location/coarseLocation: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
        const raw = status.location ?? status.coarseLocation;
        const mapped: 'granted' | 'denied' | 'prompt' =
          raw === 'granted' ? 'granted'
          : raw === 'denied' ? 'denied'
          : 'prompt';
        set({ permissionState: mapped, permissionGranted: mapped === 'granted' });
      } catch {
        set({ permissionState: 'unknown' });
      }
      return;
    }
    // Web: use Permissions API
    if (!navigator.permissions) {
      set({ permissionState: 'unknown' });
      return;
    }
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      set({ permissionState: status.state as any, permissionGranted: status.state === 'granted' });
      status.onchange = () => {
        set({ permissionState: status.state as any, permissionGranted: status.state === 'granted' });
      };
    } catch {
      set({ permissionState: 'unknown' });
    }
  },

  requestPermission: async () => {
    // Native: use Capacitor plugin's requestPermissions — only prompts the OS if the
    // current state is 'prompt'. If already 'granted' or 'denied', it returns that
    // state without showing a dialog. This ensures the app respects the user's past
    // choice across cold starts.
    if (Capacitor.isNativePlatform()) {
      try {
        const { Geolocation } = await import('@capacitor/geolocation');
        const result = await Geolocation.requestPermissions({ permissions: ['location'] });
        const raw = result.location ?? result.coarseLocation;
        if (raw === 'granted') {
          set({ permissionGranted: true, permissionState: 'granted' });
          // Fetch first position via the web API (watchPosition already works post-grant)
          try {
            const pos = await getCurrentPosition();
            const state = get();
            const effective = state.isManualFreq ? state.effectiveLocation : pos.coords;
            set({
              realLocation: pos.coords,
              effectiveLocation: effective,
              frequency: computeFrequency(effective),
              accuracy: pos.accuracy,
            });
          } catch { /* GPS not yet warm — watchPosition will fill it in */ }
          return true;
        }
        set({ permissionGranted: false, permissionState: raw === 'denied' ? 'denied' : 'prompt' });
        return false;
      } catch {
        set({ permissionGranted: false, permissionState: 'unknown' });
        return false;
      }
    }

    // Web: the browser Permissions API doesn't expose a request, so invoking
    // getCurrentPosition is the canonical way to trigger the permission prompt.
    try {
      const result = await getCurrentPosition();
      const real = result.coords;
      const state = get();
      const effective = state.isManualFreq ? state.effectiveLocation : real;
      set({
        realLocation: real,
        effectiveLocation: effective,
        frequency: computeFrequency(effective),
        accuracy: result.accuracy,
        permissionGranted: true,
        permissionState: 'granted',
      });
      return true;
    } catch (err: unknown) {
      const geoErr = err as { code?: number };
      if (geoErr?.code === 1) {
        // PERMISSION_DENIED — user actually blocked location access.
        set({ permissionGranted: false, permissionState: 'denied' });
        return false;
      }
      // TIMEOUT / POSITION_UNAVAILABLE — permission likely granted, GPS not warm yet.
      set({ permissionGranted: true, permissionState: 'granted' });
      return true;
    }
  },

  startWatching: () => {
    let lastUpdateMs = 0;
    const cleanup = watchPosition(
      (result) => {
        const now = Date.now();
        const intervalMs = get().syncIntervalSec * 1000;
        // Always store real location, but throttle effective/frequency updates
        const real = result.coords;
        const state = get();
        if (!state.permissionGranted || now - lastUpdateMs >= intervalMs) {
          lastUpdateMs = now;
          const effective = state.isManualFreq ? state.effectiveLocation : real;
          set({
            realLocation: real,
            effectiveLocation: effective,
            frequency: computeFrequency(effective),
            accuracy: result.accuracy,
            permissionGranted: true,
          });
        } else {
          // Silently update real location without changing frequency display
          set({ realLocation: real, accuracy: result.accuracy, permissionGranted: true });
        }
      },
      (err) => {
        if (err.code === 1) set({ permissionGranted: false });
        // Suppress repeated timeout/unavailable errors if we already have a location
        else if (!get().realLocation) {
          console.warn('[Location] Watch error:', err.message);
        }
      },
    );
    return cleanup;
  },

  fetchOnce: async () => {
    try {
      const result = await getCurrentPosition();
      const real = result.coords;
      const state = get();
      const effective = state.isManualFreq ? state.effectiveLocation : real;
      set({
        realLocation: real,
        effectiveLocation: effective,
        frequency: computeFrequency(effective),
        accuracy: result.accuracy,
        permissionGranted: true,
      });
    } catch (err) {
      // Only log once if we already have a location (suppress repeated timer errors)
      if (!get().realLocation) {
        console.warn('[Location] Fetch failed:', (err as any)?.message ?? err);
      }
    }
  },

  setRadarRadius: (km) => {
    set({ radarRadius: km });
    if (currentUid) saveUserSettings(currentUid, { radarRadius: km });
  },

  setMessageReceptionRadius: (km) => {
    set({ messageReceptionRadius: km });
    if (currentUid) saveUserSettings(currentUid, { messageReceptionRadius: km });
  },

  setReceptionMode: (mode) => {
    set({ receptionMode: mode });
    if (currentUid) saveUserSettings(currentUid, { receptionMode: mode });
  },

  setSyncInterval: (sec) => set({ syncIntervalSec: sec }),

  loadUserSettings: (uid) => {
    if (currentUid === uid) return; // Already loaded for this user
    currentUid = uid;
    try {
      const raw = localStorage.getItem(SETTINGS_KEY_PREFIX + uid);
      if (raw) {
        const saved = JSON.parse(raw);
        const patch: Partial<LocationState> = {};
        if (typeof saved.radarRadius === 'number' && saved.radarRadius > 0) {
          patch.radarRadius = saved.radarRadius;
        }
        if (typeof saved.messageReceptionRadius === 'number' && saved.messageReceptionRadius > 0) {
          patch.messageReceptionRadius = saved.messageReceptionRadius;
        }
        if (saved.receptionMode === 'blur_beyond' || saved.receptionMode === 'sensitivity_only') {
          patch.receptionMode = saved.receptionMode;
        }
        if (Object.keys(patch).length > 0) {
          set(patch);
        }
      }
    } catch {}
  },

  resetUserSettings: () => {
    currentUid = null;
    set({ radarRadius: 5, messageReceptionRadius: 100, receptionMode: 'blur_beyond' });
  },

  teleport: (frequency, jumpRange?: { min: number; max: number }) => {
    const coords = frequencyToCoords(frequency);
    if (!coords) return false;

    // Apply random offset within jump range
    let finalCoords = coords;
    const range = jumpRange ?? { min: 0, max: 0 };
    if (range.max > 0) {
      const dist = range.min + Math.random() * (range.max - range.min);
      const angle = Math.random() * 2 * Math.PI;
      // ~111,320 meters per degree latitude
      const dLat = (dist * Math.cos(angle)) / 111320;
      const dLng = (dist * Math.sin(angle)) / (111320 * Math.cos(coords.lat * Math.PI / 180));
      finalCoords = { lat: coords.lat + dLat, lng: coords.lng + dLng };
    }

    // Validate final coordinates after offset — reject if out of bounds or
    // encoding fails (e.g. Infinity from cos(lat)≈0 at extreme latitudes)
    const finalFreq = coordsToFrequency(finalCoords.lat, finalCoords.lng);
    if (finalFreq === 'N/A') return false;

    set({
      isManualFreq: true,
      manualFrequency: frequency,
      effectiveLocation: finalCoords,
      frequency: finalFreq,
    });
    // Tell the sync/notification loop to fire immediately so the connection
    // notification appears at the new location without waiting for the 10s tick.
    try { window.dispatchEvent(new CustomEvent('ourhertz-teleported')); } catch { /* SSR / no window */ }
    return true;
  },

  cancelTeleport: () => {
    const real = get().realLocation;
    set({
      isManualFreq: false,
      manualFrequency: null,
      effectiveLocation: real,
      frequency: computeFrequency(real),
    });
  },
}));
